import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuItem,
} from "@/components/UI/DropdownMenu";
import { ROUTES } from "@/constants/navigation";
import type { AppTheme } from "@/constants/theme";
import { useUser } from "@/contexts/UserContext";
import { useLanguage } from "@/hooks/useLanguage";
import { useTheme } from "@/hooks/useTheme";
import type { User } from "@/types";
import "./AccountProfileMenu.css";

const PROFILE_MENU_ID = "account-profile-menu";

export function AccountProfileMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    session: { loading, user, logout },
    isInterfaceBuilderMode,
    toggleInterfaceBuilderMode,
  } = useUser();
  const { lang, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [menuState, setMenuState] = useState<"closed" | "open">("closed");
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = menuState === "open";
  const displayName = loading
    ? t("profile.loadingName")
    : getDisplayName(user, t("profile.guest"));
  const email = loading
    ? t("profile.loadingEmail")
    : getDisplayEmail(user, t("profile.emailMissing"));
  const initials = getInitials(displayName);

  const closeMenu = () => setMenuState("closed");
  const toggleMenu = () => {
    setMenuState((currentState) => (
      currentState === "open" ? "closed" : "open"
    ));
  };

  const handleToggleInterfaceBuilder = () => {
    toggleInterfaceBuilderMode();
    closeMenu();
  };
  const handleToggleTheme = () => {
    toggleTheme();
    closeMenu();
  };
  const handleToggleLanguage = () => {
    toggleLanguage();
    closeMenu();
  };

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      if (
        target instanceof Node
        && !rootRef.current?.contains(target)
      ) {
        closeMenu();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      closeMenu();
      buttonRef.current?.focus();
    };

    window.addEventListener("pointerdown", handlePointerDown);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleLogout = async () => {
    closeMenu();
    await logout();
    navigate(ROUTES.login, { replace: true });
  };

  return (
    <div
      className={`account-profile ${open ? "account-profile--open" : ""}`}
      ref={rootRef}
    >
      <button
        ref={buttonRef}
        type="button"
        className="account-profile__trigger"
        aria-label={t("profile.openMenu", { name: displayName, email })}
        aria-controls={open ? PROFILE_MENU_ID : undefined}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={toggleMenu}
      >
        <span className="account-profile__avatar" aria-hidden="true">
          {initials}
        </span>
        <span className="account-profile__summary">
          <span className="account-profile__name">{displayName}</span>
          <span className="account-profile__email">{email}</span>
        </span>
        <span className="account-profile__chevron" aria-hidden="true">
          <AccountProfileChevron />
        </span>
      </button>

      {open ? (
        <DropdownMenu
          id={PROFILE_MENU_ID}
          label={t("profile.menuLabel")}
          className="account-profile__menu"
        >
          <DropdownMenuItem
            icon={<AccountProfileIcon name="interfaceBuilder" />}
            checked={isInterfaceBuilderMode}
            onClick={handleToggleInterfaceBuilder}
          >
            {t("profile.interfaceBuilder")}
          </DropdownMenuItem>

          <DropdownMenuItem
            icon={<AccountProfileIcon name={theme} />}
            value={t(`profile.themeValue.${theme}`)}
            onClick={handleToggleTheme}
          >
            {t("profile.theme")}
          </DropdownMenuItem>

          <DropdownMenuItem
            icon={<AccountProfileIcon name="language" />}
            value={lang.toUpperCase()}
            onClick={handleToggleLanguage}
          >
            {t("profile.language")}
          </DropdownMenuItem>

          <DropdownMenuItem
            icon={<AccountProfileIcon name="logout" />}
            variant="danger"
            onClick={handleLogout}
          >
            {t("auth.logoutBtn")}
          </DropdownMenuItem>
        </DropdownMenu>
      ) : null}
    </div>
  );
}

type AccountProfileIconName =
  | AppTheme
  | "interfaceBuilder"
  | "language"
  | "logout";

function AccountProfileIcon({ name }: { name: AccountProfileIconName }) {
  if (name === "dark") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
      </svg>
    );
  }

  if (name === "light") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <circle cx="12" cy="12" r="5" />
        <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
      </svg>
    );
  }

  if (name === "language") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M5 8h14" />
        <path d="M12 4v4" />
        <path d="M7 8c1 5 3 8 5 8s4-3 5-8" />
        <path d="M5 20l4-8 4 8" />
        <path d="M7 16h4" />
      </svg>
    );
  }

  if (name === "interfaceBuilder") {
    return (
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <rect x="4" y="4" width="6" height="6" rx="1.5" />
        <rect x="14" y="4" width="6" height="6" rx="1.5" />
        <rect x="4" y="14" width="6" height="6" rx="1.5" />
        <path d="M14 17h6" />
        <path d="M17 14v6" />
      </svg>
    );
  }

  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <path d="M16 17l5-5-5-5" />
      <path d="M21 12H9" />
    </svg>
  );
}

function getDisplayName(user: User | null, fallback: string): string {
  const fullName = [user?.first_name, user?.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();

  return fullName || user?.username || user?.name || user?.email || fallback;
}

function getDisplayEmail(user: User | null, fallback: string): string {
  return user?.email || fallback;
}

function getInitials(displayName: string): string {
  const [firstWord, secondWord] = displayName.trim().split(/\s+/);
  const firstInitial = firstWord?.charAt(0) ?? "U";
  const secondInitial = secondWord?.charAt(0) ?? firstWord?.charAt(1) ?? "";

  return `${firstInitial}${secondInitial}`.toLocaleUpperCase();
}

function AccountProfileChevron() {
  return (
    <svg width="9" height="5" viewBox="0 0 9 5" fill="none" aria-hidden>
      <path d="M1 1L4.5 4L8 1" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}
